import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Gallery from "../components/Gallery";
import OrderForm from "../components/OrderForm";
import { FaFire, FaShippingFast, FaLeaf, FaAward, FaVolumeUp, FaVolumeMute } from "react-icons/fa";
import useDocumentMeta from "../hooks/useDocumentMeta";

export default function Home() {
    const { t } = useTranslation();
    const [soundOn, setSoundOn] = useState(true);

    useDocumentMeta({
        title: t("seo.home.title"),
        description: t("seo.home.description"),
        canonicalPath: "/",
        robots: "index,follow,max-image-preview:none",
    });

    const toggleSound = () => {
        const audio = document.getElementById("ambient-sound");
        if (!audio) return;

        if (soundOn) {
            audio.muted = true;
            setSoundOn(false);
        } else {
            audio.muted = false;
            audio.play().catch(() => { });
            setSoundOn(true);
        }
    };

    useEffect(() => {
        const audio = document.getElementById("ambient-sound");
        if (!audio) return;

        const tryPlay = () => {
            audio.volume = 0.3;
            audio.play().catch(() => { });
            window.removeEventListener("click", tryPlay);
        };

        // wait for any user interaction
        window.addEventListener("click", tryPlay);

        return () => window.removeEventListener("click", tryPlay);
    }, []);

    const features = [
        {
            icon: FaFire,
            title: t('features.premiumQuality.title'),
            description: t('features.premiumQuality.description')
        },
        {
            icon: FaShippingFast,
            title: t('features.fastDelivery.title'),
            description: t('features.fastDelivery.description')
        },
        {
            icon: FaLeaf,
            title: t('features.ecoFriendly.title'),
            description: t('features.ecoFriendly.description')
        },
        {
            icon: FaAward,
            title: t('features.industryTrusted.title'),
            description: t('features.industryTrusted.description')
        }
    ];
    return (
        <main className="bg-vsblack text-white overflow-hidden">
            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center text-center">
                {/* 🔥 Background video */}
                <video
                    className="absolute inset-0 w-full h-full object-cover"
                    src="/videos/bg.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                />

                {/* Optional dark overlay for better contrast */}
                <div className="absolute inset-0 bg-black/60"></div>

                {/* 🔊 Hidden ambient audio */}
                <audio id="ambient-sound" src="/audio/charcoal-ambient.mp3" loop />

                {/* 🔘 Sound toggle button */}
                <button
                    onClick={toggleSound}
                    className="
        absolute top-4 right-4 z-20
        text-gray-400 text-2xl
        hover:text-gray-200
        hover:scale-110
        transition duration-300
    "
                    aria-label={soundOn ? "Mute ambient sound" : "Unmute ambient sound"}
                >
                    {soundOn ? <FaVolumeUp /> : <FaVolumeMute />}
                </button>
                {/* Animated background glow */}
                <div className="absolute inset-0 overflow-hidden">
                    <motion.div
                        className="absolute top-1/2 left-1/2 w-[800px] h-[800px] -translate-x-1/2 -translate-y-1/2 bg-red-glow opacity-30 rounded-full blur-3xl"
                        animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0.3, 0.5, 0.3],
                        }}
                        transition={{
                            duration: 8,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                    />
                </div>

                {/* Hero content */}
                <div className="relative z-10 max-w-5xl px-6 py-20">
                    <motion.div
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.8 }}
                    >
                        <motion.h1
                            className="text-6xl md:text-7xl lg:text-8xl font-extrabold mb-6 font-serif"
                            initial={{ y: 30, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            <span className="text-vsred hero-text-shadow">{t('hero.title')}</span>
                            <br />
                            <span className="text-white text-5xl md:text-6xl">{t('hero.subtitle')}</span>
                        </motion.h1>

                        <motion.p
                            className="text-gray-300 text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed"
                            initial={{ y: 30, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            {t('hero.description')}
                        </motion.p>

                        <motion.div
                            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                            initial={{ y: 30, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                        >
                            <a
                                href="#quote"
                                className="btn-primary text-lg"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (window.location.pathname === '/') {
                                        // On home page, scroll to order form
                                        document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' });
                                    } else {
                                        // On other pages, scroll to top
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }
                                }}
                            >
                                {t('hero.requestQuote')}
                            </a>
                            <a
                                href="/products"
                                className="px-8 py-3 rounded-lg border-2 border-vsred text-vsred-light font-semibold hover:bg-vsred hover:text-white transition-all duration-300"
                            >
                                {t('hero.viewProducts')}
                            </a>
                        </motion.div>
                    </motion.div>

                    {/* Scroll indicator */}
                    <motion.div
                        className="flex justify-center mt-6 md:mr-3"
                        animate={{ y: [0, 10, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        <div className="w-6 h-10 border-2 border-gray-600 rounded-full flex justify-center">
                            <div className="w-1 h-3 bg-vsred rounded-full mt-2 animate-pulse" />
                        </div>
                    </motion.div>
                </div>

            </section>

            {/* Features Section */}
            <section className="py-20 px-6">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-bold font-serif mb-4 text-vsred hero-text-shadow">
                            {t('features.title')}
                        </h2>
                        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                            {t('features.subtitle')}
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ y: 50, opacity: 0 }}
                                whileInView={{ y: 0, opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                whileHover={{ y: -10 }}
                                className="card-dark p-8 text-center group"
                            >
                                <div className="mb-4 flex justify-center">
                                    <div className="w-16 h-16 bg-gradient-to-br from-vsred to-vsred-dark rounded-xl flex items-center justify-center shadow-red-glow group-hover:scale-110 transition-transform duration-300">
                                        <feature.icon className="text-3xl text-white" />
                                    </div>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-white font-serif">
                                    {feature.title}
                                </h3>
                                <p className="text-gray-400 leading-relaxed">
                                    {feature.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Gallery Section */}
            <div id="products">
                <Gallery />
            </div>

            {/* Quote / Order Form */}
            <OrderForm />
        </main>
    );
}
